import React, { useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export function AppLayout({ title }) {
  const { user, logout, hasPlan } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };
  const closeSidebar = () => setSidebarOpen(false);

  const initials = user?.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';

  // Navegación dinámica según rol y plan
  const buildNav = () => {
    if (user?.role === 'super_admin') {
      return [
        { label: 'Principal', items: [
          { path: '/superadmin', icon: '📊', label: 'Dashboard' },
          { path: '/superadmin/businesses', icon: '🏪', label: 'Negocios' },
          { path: '/superadmin/plans', icon: '📋', label: 'Planes' },
          { path: '/superadmin/users', icon: '👥', label: 'Usuarios' },
        ]},
        { label: 'Gestion', items: [
          { path: '/superadmin/reports', icon: '📈', label: 'Reportes' },
          { path: '/superadmin/tickets', icon: '🎫', label: 'Soporte' },
        ]},
      ];
    }

    if (user?.role === 'barbero') {
      return [
        { label: 'Mi Agenda', items: [
          { path: '/barber/calendar',      icon: '📅', label: 'Calendario' },
          { path: '/barber/appointments',  icon: '✂️', label: 'Citas' },
        ]},
        { label: 'Soporte', items: [
          { path: '/barber/support', icon: '🎫', label: 'Soporte' },
        ]},
      ];
    }

    // admin y admin_general — filtrar por plan
    const operacion = [
      { path: '/admin/calendar', icon: '📅', label: 'Calendario' },
      { path: '/admin/appointments', icon: '✂️', label: 'Citas' },
      { path: '/admin/clients', icon: '👤', label: 'Clientes' },
    ].filter(Boolean);

    const configuracion = [
      { path: '/admin/services', icon: '💈', label: 'Servicios' },
      hasPlan('has_workers') && { path: '/admin/workers', icon: '👥', label: 'Trabajadores' },
      hasPlan('has_multi_branch') && { path: '/admin/branches', icon: '🏪', label: 'Sucursales' },
      { path: '/admin/hours', icon: '🕐', label: 'Horarios' },
      { path: '/admin/business', icon: '⚙️', label: 'Mi Negocio' },
    ].filter(Boolean);

    const finanzas = [
      hasPlan('has_finance') && { path: '/admin/finance', icon: '💰', label: 'Finanzas' },
    ].filter(Boolean);

    const analisis = [
      hasPlan('has_reports') && { path: '/admin/reports', icon: '📈', label: 'Reportes' },
      hasPlan('has_advanced_reports') && { path: '/admin/advanced-reports', icon: '📊', label: 'Reportes Avanzados' },
    ].filter(Boolean);

    const isPremiumAdmin = user?.role === 'admin' && user?.plan_name?.toLowerCase() === 'premium';

    const sections = [
      ...(isPremiumAdmin ? [{ label: 'Dashboard', items: [
        { path: '/admin', icon: '📊', label: 'Dashboard' },
      ]}] : []),
      { label: 'Operacion', items: operacion },
      { label: 'Configuracion', items: configuracion },
    ];
    if (finanzas.length > 0) sections.push({ label: 'Finanzas', items: finanzas });
    if (analisis.length > 0) sections.push({ label: 'Analisis', items: analisis });
    sections.push({ label: 'Soporte', items: [
      { path: '/admin/support', icon: '🎫', label: 'Soporte' },
    ]});
    return sections;
  };

  const navSections = buildNav();

  return (
    <div className="app-layout">
      <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={closeSidebar} />

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <div className="sidebar-logo-icon">💈</div>
          <div className="sidebar-logo-text">
            BarberSaaS
            <span>Panel de Control</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {navSections.map(section => (
            <div key={section.label}>
              <div className="sidebar-section-label">{section.label}</div>
              {section.items.map(item => (
                <button
                  key={item.path}
                  className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
                  onClick={() => { navigate(item.path); closeSidebar(); }}
                >
                  <span className="sidebar-icon">{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-user-avatar">{initials}</div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.name}</div>
              <div className="sidebar-user-role">{user?.role?.replace(/_/g, ' ')}</div>
            </div>
          </div>
          <button className="sidebar-link" style={{ marginTop: 8 }} onClick={handleLogout}>
            <span className="sidebar-icon">🚪</span>Cerrar sesion
          </button>
        </div>
      </aside>

      <div className="main-content">
        <header className="topbar">
          <button className="hamburger" onClick={() => setSidebarOpen(o => !o)}>☰</button>
          <span className="topbar-title">{title || 'Panel'}</span>
          <div className="topbar-actions">
            <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
              {user?.business_name || 'BarberSaaS'}
            </span>
          </div>
        </header>
        <main className="page-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default AppLayout;
